import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import { Modal } from 'react-bootstrap';
import  RawMaterialItems from '../../../pages/Items/RawMaterialItems'; 

import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import ItemInput from '../../Common/ItemInput';
import CommonInput from '../../Common/CommonInput';
import CommonDate from '../../Common/CommonDate';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';
import CommonSelect from '../../Common/CommonSelect';

import { fetchSettings } from '../../../store/modules/stockissue/stockissueActions';
import useFormSettings from '../../../hooks/useFormSettings';

import jobOrderService from '../../../services/joborderService';
import tailorService from '../../../services/tailorService';
import cuttingMasterService from '../../../services/cuttingmasterService';

import { 
    calculateItemValues,
    calculateGrossTotal,
} from './StockIssueCalculation';

import useModal from '../../../hooks/useModal';
import DataSelectionModal from '../../Common/CommonModal/DataSelectionModal';
import AllJobOrders from '../../../pages/Alljoborders/Alljoborders';
import { useHasPermission } from '../../../utils/permissionUtils';
const StockIssueForm = ({ formLabel = 'Create Stock Issue', initialData, onSave, isLoading, onClose, customActionsDispatch = {} }) => {
    
    const hasCreatePermission = useHasPermission(['create stock issues']);
    const hasUpdatePermission = useHasPermission(['update stock issues']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('stockissue', fetchSettings);
    const { selected, issued_to_list } = settings || {};

    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
    
    const [formData, setFormData] = useState({
        id: '',
        issue_number: '',
        issue_date: '',
        job_card_description: {},
        job_order: null,
        job_order_date: '',
        issued_to: null,
        issued_to_ref: null,
        fg_item: null,
        fg_qty: '',
        qty_to_update: '',
        fg_unit_price: '',
        fg_description: {},
        material_total: '',
        materials: [],
        items: [],

        unit_price: '',
        unit_percentage: '',
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingFGItems, setIsLoadingFGItems] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || '',
                issue_number: initialData.issue_number || '',
                issue_date: initialData.initialType === 'create' ? 
                    new Date().toISOString().split('T')[0] : 
                    initialData.issue_date || '',
                job_card_description: initialData.job_card_description || {},
                job_order: initialData.job_order || null,
                job_order_date: initialData.job_order_date || '',
                issued_to: initialData.issued_to || null,
                issued_to_ref: initialData.issued_to_ref || null,
                fg_item: initialData.fg_item || null,
                fg_qty: initialData.fg_qty || '',
                qty_to_update: initialData.qty_to_update || '',
                fg_unit_price: initialData.fg_unit_price || '',
                fg_description: initialData.fg_description || {},
                material_total: initialData.material_total || '',
                materials: initialData.materials || [],
                items: initialData.items || [],

                unit_price: initialData.unit_price || '',
                unit_percentage: initialData.unit_percentage || '',
            });
        }
    }, [initialData]);

    const calculateFormData = (formData) => {
        const materialTotal = calculateGrossTotal(formData.materials);
        return {
            material_total: materialTotal.toFixed(3),
        };
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const itemFieldMatch = name.match(/^materials\[(\d+)\]\.(.+)$/);

        setFormData(prev => {
            let newData = { ...prev };

            if (itemFieldMatch) {
                const itemIndex = parseInt(itemFieldMatch[1]);
                const field = itemFieldMatch[2];
                
                const newItems = [...prev.materials];
                const updatedItem = {
                    ...newItems[itemIndex],
                    [field]: type === 'checkbox' ? checked : value
                };

                // Only reset calculated values if the field is truly empty (not zero)
                if (['quantity', 'unit_price'].includes(field) && (value === '' || value === 0 || value === '0')) {
                    updatedItem.line_amount = '0.000';
                } else if (['quantity', 'unit_price'].includes(field)) {
                    const calculatedValues = calculateItemValues(updatedItem);
                    Object.assign(updatedItem, calculatedValues);
                }

                newItems[itemIndex] = updatedItem;
                newData.materials = newItems;
            } else {
                newData[name] = type === 'checkbox' ? checked : value;
            }

            const calculatedData = calculateFormData(newData);
            return {
                ...newData,
                ...calculatedData
            };
        });

        // Validate qty_to_update against remaining quantity
        if (name === 'qty_to_update' && formData.fg_item) {
            const totalQuantity = parseFloat(formData.fg_item.total_quantity) || 0;
            const newQty = parseFloat(value) || 0;
            const currentProduced = formData.issued_to?.id === 'tailor' 
                ? parseFloat(formData.fg_item.tailor) || 0
                : formData.issued_to?.id === 'cutting_master'
                    ? parseFloat(formData.fg_item.cutting_master) || 0
                    : 0;

            // For update case, subtract the existing quantity from currentProduced
            const existingQty = initialData?.id ? parseFloat(initialData.fg_qty) || 0 : 0;
            const adjustedCurrentProduced = currentProduced - existingQty;

            if (newQty + adjustedCurrentProduced > totalQuantity) {
                setErrors(prev => ({
                    ...prev,
                    qty_to_update: `Quantity cannot exceed remaining quantity of ${(totalQuantity - adjustedCurrentProduced).toFixed(3)}`
                }));
            } else if (errors.qty_to_update) {
                setErrors(prev => ({...prev, qty_to_update: ''}));
            }
        } else if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleFgPercentageChange = async (e) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => {
            let newData = { ...prev };

            return {
                ...newData,
                [name]: value,
                fg_unit_price: ((value * newData.unit_price) / 100).toFixed(3),
            };
        })
    }

    const handleSelectChange = async (name, value) => {
        if(name === 'issued_to') {
            setFormData(prev => {
                return {
                    ...prev,
                    issued_to_ref: null,
                    [name]: value,
                };
            });
        } else if(name === 'issued_to_ref') {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        } else if(name === 'job_order') {
            setFormData(prev => ({
                ...prev,
                job_order_date: value.order_date,
                [name]: value
            }));
            
            if (value) {
                try {
                    setIsLoadingFGItems(true);
                    const response = await jobOrderService.getFGItems(value.id);
                    setFormData(prev => ({
                        ...prev,
                        items: response.data,
                        fg_item: null,
                        fg_qty: '',
                        qty_to_update: ''
                    }));
                } catch (error) {
                    console.error('Error fetching FG items:', error);
                } finally {
                    setIsLoadingFGItems(false);
                }
            }
        } else if(name === 'fg_item') {
            setFormData(prev => ({
                ...prev,
                unit_price: value ? value.unit_price : '',
                unit_percentage: value ? value.unit_percentage : '',
                fg_unit_price: value ? value.production_charge : '',
                [name]: value
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate qty_to_update before submission
        if (formData.fg_item && formData.issued_to) {
            const totalQuantity = parseFloat(formData.fg_item.total_quantity) || 0;
            const newQty = parseFloat(formData.qty_to_update) || 0;
            const currentProduced = formData.issued_to.id === 'tailor' 
                ? parseFloat(formData.fg_item.tailor) || 0
                : formData.issued_to.id === 'cutting_master'
                    ? parseFloat(formData.fg_item.cutting_master) || 0
                    : 0;

            // For update case, subtract the existing quantity from currentProduced
            const existingQty = initialData?.id ? parseFloat(initialData.fg_qty) || 0 : 0;
            const adjustedCurrentProduced = currentProduced - existingQty;

            if (newQty + adjustedCurrentProduced > totalQuantity) {
                setErrors(prev => ({
                    ...prev,
                    qty_to_update: `Quantity cannot exceed remaining quantity of ${(totalQuantity - adjustedCurrentProduced).toFixed(3)}`
                }));
                return;
            }
        }

        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || isLoadingFGItems || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);


    const jobOrderModal = useModal();
    const jobOrderExternalCondition = {
        rules: {
            condition: 'and',
            children: [
                {
                    type: 'rule',
                    query: {
                        operator: 'in',
                        rule: 'job_orders.status',
                        type: 'text',
                        value: ['1', '2', '3', '4']
                    }
                }
            ]
        }
    };

    const handleJobOrderSelected = (jobOrder) => {
        const updatedValue = {
            id: jobOrder.id,
            label: jobOrder.order_number +' ( '+jobOrder.customer_name+' )',
            order_date: jobOrder.order_date
        }
        handleSelectChange('job_order', updatedValue);
        jobOrderModal.hide();
    };



    const itemModal = useModal();
    const handleItemSelected = (selectedItems) => {

        // Get current items from form data
        const currentItems = formData.items || [];
        
        // Filter out items that already exist in the form
        const newItems = selectedItems.filter(selectedItem => 
            !currentItems.some(existingItem => existingItem.item?.id === selectedItem.id)
        );

        // Transform new items to match the form data structure
        const transformedItems = newItems.map(item => ({
                id: '',
                item: {
                    id: item.id,
                    item_code: item.item_code,
                    label: item.name?.en || '',
                    available_stock: item.available_stock || 0
                },
                item_description: '',
                unit: item.measurement_unit?.label || '',
                quantity : '',
                unit_price: item.unit_cost || '0.000',
                line_amount : '',
            }
        ));

        // Update form data with new items
        setFormData(prev => ({
            ...prev,
            materials: [...prev.materials, ...transformedItems]
        }));
    };


    const handleRemoveItem = (index) => {
        setFormData(prev => {
            const newItems = [...prev.materials];
            newItems.splice(index, 1);
            
            const newData = {
                ...prev,
                materials: newItems
            };

            const calculatedData = calculateFormData(newData);
            return {
                ...newData,
                ...calculatedData
            };
        });
    };

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">{formLabel}</h5>
                </div>
                <div className="d-flex justify-content-end">
                    {/* <LanguageSwitch onLanguageChange={setSelectedLanguage} /> */}
                </div>
            </div>
            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="issue_number"
                            label="Issue Number"
                            value={formData.issue_number}
                            onChange={handleChange}
                            placeholder="Enter issue number"
                            disabled={true}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="issue_date"
                            label="Issue Date"
                            value={formData.issue_date}
                            onChange={handleChange}
                            placeholder="Enter issue date"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Description"
                            name="job_card_description"
                            value={formData.job_card_description}
                            onChange={handleChange}
                            errors={errors}
                            required={false}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            rows={1}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonAsyncSelect
                            name="job_order"
                            label="Job Order"
                            value={formData.job_order}
                            onChange={(value) => handleSelectChange('job_order', value)}
                            loadOptions={async (inputValue) => {
                                const condition = {
                                    rules: {
                                        condition: 'and',
                                        children: [
                                            {
                                                type: 'rule',
                                                query: {
                                                    operator: 'in',
                                                    rule: 'job_orders.status',
                                                    type: 'text',
                                                    value: ['1', '2', '3', '4']
                                                }
                                            }
                                        ]
                                    }
                                }

                                const response = await jobOrderService.getAllList({ search: inputValue }, condition);
                                return response.data.map(job => ({
                                    id: job.id,
                                    label: job.order_number +' ( '+job.customer_name+' )',
                                    order_date: job.order_date
                                }));
                            }}
                            isSubmitting={isSubmitting || formData?.id}
                            errors={errors}
                            required={true}
                            actionButtons={[
                                {
                                    icon: 'fa fa-bars',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => jobOrderModal.show()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="job_order_date"
                            label="Job Order Date"
                            value={formData.job_order_date}
                            onChange={handleChange}
                            placeholder="Enter job order date"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="issued_to"
                            label="Issued To"
                            value={formData.issued_to}
                            onChange={(value) => handleSelectChange('issued_to', value)}
                            options={issued_to_list}
                            isSubmitting={isSubmitting || formData?.id}
                            errors={errors}
                            required={true}
                            placeholder="Select issued to..."
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonAsyncSelect
                            name="issued_to_ref"
                            label="Issued To Ref."
                            value={formData.issued_to_ref}
                            onChange={(value) => handleSelectChange('issued_to_ref', value)}
                            loadOptions={async (inputValue) => {
                                if (formData.issued_to?.id === 'tailor') {
                                    const response = await tailorService.getList({ search: inputValue });
                                    return response.data.map(employee => ({
                                        id: employee.id,
                                        label: employee.name?.en+' ('+employee.employee_code+')'
                                    }));
                                } else if (formData.issued_to?.id === 'cutting_master') {
                                    const response = await cuttingMasterService.getList({ search: inputValue });
                                    return response.data.map(employee => ({
                                        id: employee.id,
                                        label: employee.name?.en+' ('+employee.code+')'
                                    }));
                                } else {
                                    return [];
                                }
                            }}
                            isSubmitting={!formData.issued_to ? true : isSubmitting}
                            errors={errors}
                            required={true}
                            cacheOptions={false}
                            disabled={!!initialData?.id}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="FG Description"
                            name="fg_description"
                            value={formData.fg_description}
                            onChange={handleChange}
                            errors={errors}
                            required={false}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            rows={1}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-3">
                        <CommonSelect
                            name="fg_item"
                            label="FG Item"
                            options={formData.items}
                            value={formData.fg_item}
                            onChange={(value) => handleSelectChange('fg_item', value)}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-3">
                        <CommonInput
                            name="qty_to_update"
                            type="number"
                            label={`FG Qty to Issue: ${formData?.id ? `[ Issued: ${formData.fg_qty} ]` : ''}`}
                            value={formData.qty_to_update}
                            onChange={handleChange}
                            errors={errors}
                            required={formData?.id ? false : true}
                            disabled={isSubmitting || !formData.fg_item}
                            placeholder={`${formData?.id ? `Leave Empty to not update the existing quantity` : `Enter fg item quantity`}`} 
                        />
                    </div>
                    <div className="col-md-3">
                        <CommonInput
                            name="fg_unit_price"
                            type="number"
                            label="Unit Price (Labour per unit)"
                            value={(formData.issued_to?.id === 'tailor') ? formData.fg_unit_price : '0'}
                            onChange={handleChange}
                            errors={errors}
                            required={false}
                            disabled={isSubmitting || formData.unit_percentage > 0 || formData.issued_to?.id !== 'tailor'}
                            placeholder="Enter unit price"
                        />
                    </div>
                    <div className="col-md-3">
                        <CommonInput
                            name="unit_percentage"
                            type="number"
                            label="Unit Percentage (Labour per unit)"
                            value={(formData.issued_to?.id === 'tailor') ? formData.unit_percentage : '0'}
                            onChange={handleFgPercentageChange}
                            errors={errors}
                            required={false}
                            disabled={isSubmitting || formData.issued_to?.id !== 'tailor'}
                            placeholder="Enter unit percentage"
                        />
                    </div>
                </div>

                <div className="row">
                    <div className='col-12 col-xl-12 mt-4'>
                        {hasPermission && (
                        <button type="button" className="btn btn-primary" onClick={() => itemModal.show()}>
                            Add Raw Material
                        </button>
                        )}
                        {errors && errors?.['materials'] && (
                            <div className="text-danger mt-2">
                                {Array.isArray(errors['materials']) ? errors['materials'][0] : errors['materials']}
                            </div>
                        )}


                        <div className="table-responsive mt-4">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th className="text-nowrap p-3">Item Code</th>
                                        <th className="text-nowrap p-3">Item Name</th>
                                        <th className="text-nowrap p-3">Unit</th>
                                        <th className="text-nowrap p-3" style={{ width: '150px' }}>Qty</th>
                                        <th className="text-nowrap p-3" style={{ width: '150px' }}>Unit Price</th>
                                        <th className="text-nowrap p-3" style={{ width: '150px' }}>Total</th>
                                        <th className="text-nowrap p-3" style={{ width: '100px' }}>Remove</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.materials && formData.materials.length > 0 ? (
                                        formData.materials.map((item, index) => (
                                            <tr key={index}>
                                                <td className="text-nowrap p-3 align-middle">{item.item?.item_code || 'N/A'}</td>
                                                <td className="text-nowrap p-3 align-middle">{item.item?.label || 'N/A'}</td>
                                                <td className="p-3 align-middle">{item.unit || 'N/A'}</td>
                                                <td className="p-3 align-middle">
                                                    <ItemInput
                                                        index={index}
                                                        field="quantity"
                                                        value={item.quantity}
                                                        onChange={handleChange}
                                                        disabled={isSubmitting}
                                                        errors={errors}
                                                        type="number"
                                                        required={true}
                                                        displayError={false}
                                                        arrayName="materials"
                                                    />
                                                </td>
                                                <td className="p-3 align-middle">
                                                    <ItemInput
                                                        index={index}
                                                        field="unit_price"
                                                        value={item.unit_price}
                                                        onChange={handleChange}
                                                        disabled={isSubmitting}
                                                        errors={errors}
                                                        type="number"
                                                        required={true}
                                                        displayError={false}
                                                        arrayName="materials"
                                                    />
                                                </td>
                                                <td className="p-3 align-middle">
                                                    {item.line_amount}
                                                </td>
                                                <td className="p-3 align-middle">
                                                    <div>
                                                        <button 
                                                            type="button"
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => handleRemoveItem(index)}
                                                            disabled={isSubmitting}
                                                        >
                                                            <i className="fas fa-trash text-white"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="10" className="text-center">No items selected</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className='row mt-4 d-flex justify-content-end'>
                            <div className='col-md-4'>
                                <div className="mb-2 row">
                                    <label className="col-lg-6 col-md-6 col-form-label">Net Total:</label>
                                    <div className="col-lg-6 col-md-6">
                                        <CommonInput
                                            className="mb-0 custom-style text-end"
                                            name="material_total"
                                            value={formData.material_total}
                                            onChange={handleChange}
                                            disabled={true}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                <DataSelectionModal
                    show={jobOrderModal.isVisible}
                    onHide={jobOrderModal.hide}
                    title=""
                    DataComponent={AllJobOrders}
                    selectionMode="single"
                    onDataSelected={handleJobOrderSelected}
                    componentProps={{
                        // Any props specific to Suppliers component
                        excludedActions: ['create', 'actions', 'filter'],
                        externalCondition: jobOrderExternalCondition
                    }}
                />
                <DataSelectionModal
                    show={itemModal.isVisible}
                    onHide={itemModal.hide}
                    closeOnAdd={false}
                    title=""
                    DataComponent={RawMaterialItems}
                    selectionMode="multiple"
                    onRowsSelected={handleItemSelected}
                    selectionButtonText="Add Items"
                    selectionButtonIcon="fa fa-shopping-cart"
                    componentProps={{
                        excludedActions: ['create', 'actions', 'filter'],
                        columnKeys: ['name', 'item_code', 'category', 'unit_price', 'quantity_available', 'status'],
                    }}
                />

            </div>
            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading...
                        </>
                    ) : isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default StockIssueForm; 